import { getAllHalls as fetchAllHalls } from '@/services/points.service';

function normalizeHallsPayload(payload = {}) {
  const hallsByPointId = {};
  const hallsById = {};
  const tablesById = {};
  const tablesByCompositeKey = {};

  const entries = payload && typeof payload === 'object' ? Object.entries(payload) : [];

  for (const [rawPointId, rawHalls] of entries) {
    const pointId = String(rawPointId || '');
    const halls = Array.isArray(rawHalls) ? rawHalls : [];

    hallsByPointId[pointId] = halls.map((hall) => {
      const hallId = String(hall?.id || '');
      const hallName = String(hall?.name || '');

      const normalizedHall = {
        ...hall,
        id: hallId,
        pointId,
        name: hallName,
      };

      if (hallId) hallsById[hallId] = normalizedHall;

      const tables = Array.isArray(hall?.tables) ? hall.tables : [];
      for (const table of tables) {
        const tableId = String(table?.id || '');
        if (!tableId) continue;

        const num = table?.num ?? table?.number ?? null;
        const meta = {
          id: tableId,
          tableId,
          num: num == null ? null : Number(num),
          hallId: hallId || null,
          hallName: hallName || null,
          pointId: pointId || null,
        };

        tablesById[tableId] = meta;
        if (pointId) tablesByCompositeKey[`${pointId}:${tableId}`] = meta;
      }

      return normalizedHall;
    });
  }

  return {
    hallsByPointId,
    hallsById,
    tablesById,
    tablesByCompositeKey,
  };
}

function buildTableLabel(meta) {
  if (!meta) return null;
  const hallName = meta?.hallName ? String(meta.hallName) : '';
  const num = meta?.num;

  if (hallName && Number.isFinite(Number(num))) {
    return `Зал ${hallName}, стол ${Number(num)}`;
  }

  if (hallName) return hallName;
  if (Number.isFinite(Number(num))) return String(Number(num));
  return null;
}

export default {
  namespaced: true,
  state: () => ({
    merchantId: null,
    hallsByPointId: {},
    hallsById: {},
    tablesById: {},
    tablesByCompositeKey: {},
    isLoaded: false,
    isLoading: false,
    request: null,
    error: null,
  }),
  getters: {
    hallsByPointId(state) {
      return state.hallsByPointId;
    },
    isLoaded(state) {
      return state.isLoaded;
    },
    isLoading(state) {
      return state.isLoading;
    },
    error(state) {
      return state.error;
    },
    tableMetaByKey: (state) => (pointId, tableId) => {
      const normalizedPointId = pointId ? String(pointId) : '';
      const normalizedTableId = tableId ? String(tableId) : '';
      if (!normalizedTableId) return null;
      if (normalizedPointId && state.tablesByCompositeKey[`${normalizedPointId}:${normalizedTableId}`]) {
        return state.tablesByCompositeKey[`${normalizedPointId}:${normalizedTableId}`];
      }
      return state.tablesById[normalizedTableId] || null;
    },
    tableLabel:
      (state, getters) =>
      (row = {}) => {
        const pointId = row?.pointId != null ? String(row.pointId) : '';
        const candidates = [
          row?.tableId,
          row?.tableExternalId,
          row?.tableNumber,
          row?.raw?.tableId,
          row?.raw?.tableExternalId,
          row?.raw?.table?.id,
          row?.raw?.table?.tableId,
          row?.raw?.table?.number,
        ]
          .map((value) => (value == null ? '' : String(value)))
          .filter(Boolean);

        for (const candidate of candidates) {
          const meta = getters.tableMetaByKey(pointId, candidate);
          const label = buildTableLabel(meta);
          if (label) return label;
        }

        const fallback = row?.tableNumber ?? null;
        return fallback == null || String(fallback).trim() === '' ? '—' : String(fallback);
      },
  },
  mutations: {
    SET_MERCHANT_ID(state, value) {
      state.merchantId = value == null ? null : String(value);
    },
    SET_LOADING(state, value) {
      state.isLoading = Boolean(value);
    },
    SET_REQUEST(state, value) {
      state.request = value || null;
    },
    SET_ERROR(state, value) {
      state.error = value || null;
    },
    SET_DATA(state, payload) {
      state.hallsByPointId = payload?.hallsByPointId || {};
      state.hallsById = payload?.hallsById || {};
      state.tablesById = payload?.tablesById || {};
      state.tablesByCompositeKey = payload?.tablesByCompositeKey || {};
      state.isLoaded = true;
    },
    INVALIDATE_CACHE(state) {
      state.merchantId = null;
      state.hallsByPointId = {};
      state.hallsById = {};
      state.tablesById = {};
      state.tablesByCompositeKey = {};
      state.isLoaded = false;
      state.request = null;
      state.error = null;
    },
  },
  actions: {
    async ensureLoaded({ state, commit, rootGetters }, { forceRefresh = false } = {}) {
      const currentMerchantId = rootGetters?.merchantId != null ? String(rootGetters.merchantId) : null;

      if (state.merchantId && currentMerchantId && state.merchantId !== currentMerchantId) {
        commit('INVALIDATE_CACHE');
      }

      if (state.isLoaded && !forceRefresh && state.merchantId === currentMerchantId) {
        return state.hallsByPointId;
      }

      if (state.isLoading) {
        return state.request || state.hallsByPointId;
      }

      commit('SET_ERROR', null);
      commit('SET_LOADING', true);
      commit('SET_MERCHANT_ID', currentMerchantId);

      const request = fetchAllHalls();
      commit('SET_REQUEST', request);

      try {
        const payload = await request;
        const normalized = normalizeHallsPayload(payload || {});
        commit('SET_DATA', normalized);
        return normalized.hallsByPointId;
      } catch (error) {
        commit('SET_ERROR', error?.message || 'Не удалось загрузить залы и столы');
        throw error;
      } finally {
        commit('SET_REQUEST', null);
        commit('SET_LOADING', false);
      }
    },
    invalidateCache({ commit }) {
      commit('INVALIDATE_CACHE');
    },
  },
};
